import { CometChatUIKitLoginListener } from "@cometchat/chat-uikit-react";

/**
 * "Later" store — saved messages, reminders, and scheduled sends.
 * Personal and per-browser (localStorage), like Slack's "Later" list.
 * Keys are namespaced by the logged-in uid so two people sharing a
 * browser never see each other's items.
 * Honest limitation, by design for v1: reminders and scheduled sends fire
 * from the client, so they only trigger while the app is open — anything
 * past-due fires immediately on next load.
 */

export type ConversationRef = { kind: "user" | "group"; id: string; name: string };

/** Per-user localStorage key — shared with activity.ts / favourites. */
export function userScopedKey(base: string): string {
  const uid = CometChatUIKitLoginListener.getLoggedInUser()?.getUid() || "anon";
  return `${base}:${uid}`;
}

export type LaterItem = {
  id: string;
  kind: "saved" | "reminder";
  text: string;
  from: string;
  conversation: ConversationRef;
  createdAt: number;
  remindAt?: number;
  notified?: boolean;
  /** Which message this refers to — powers both the saved/unsaved state
   * on the message itself and jumping back to it. Optional because items
   * saved before this existed don't have one; those still open the
   * conversation, they just can't scroll to the exact message. */
  messageId?: number;
};

export type ScheduledSend = {
  id: string;
  text: string;
  target: ConversationRef;
  sendAt: number;
};

const ITEMS_KEY = "dbm-later-items";
const SENDS_KEY = "dbm-scheduled-sends";

type Listener = () => void;
const listeners = new Set<Listener>();

export function subscribeLater(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit() {
  listeners.forEach((fn) => fn());
}

function load<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(userScopedKey(key));
    return raw ? (JSON.parse(raw) as T[]) : [];
  } catch {
    return [];
  }
}

function save<T>(key: string, items: T[]) {
  try {
    localStorage.setItem(userScopedKey(key), JSON.stringify(items));
  } catch {
    /* storage full/blocked — degrade silently */
  }
  emit();
}

export function loadLaterItems(): LaterItem[] {
  return load<LaterItem>(ITEMS_KEY);
}

export function addLaterItem(item: Omit<LaterItem, "id" | "createdAt">): void {
  const items = loadLaterItems();
  items.unshift({ ...item, id: `l-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, createdAt: Date.now() });
  save(ITEMS_KEY, items);
}

export function removeLaterItem(id: string): void {
  save(ITEMS_KEY, loadLaterItems().filter((i) => i.id !== id));
}

/** Is this message already in the saved list? Drives the filled/outline
 * state of the bookmark on the message itself. */
export function isMessageSaved(messageId: number): boolean {
  return loadLaterItems().some((i) => i.kind === "saved" && i.messageId === messageId);
}

/** Drops the saved entry for a message — the "click the filled bookmark
 * again to unsave" half of the toggle. */
export function removeSavedMessage(messageId: number): void {
  save(
    ITEMS_KEY,
    loadLaterItems().filter((i) => !(i.kind === "saved" && i.messageId === messageId))
  );
}

export function markReminderNotified(id: string): void {
  save(
    ITEMS_KEY,
    loadLaterItems().map((i) => (i.id === id ? { ...i, notified: true } : i))
  );
}

export function loadScheduledSends(): ScheduledSend[] {
  return load<ScheduledSend>(SENDS_KEY);
}

export function addScheduledSend(send: Omit<ScheduledSend, "id">): void {
  const sends = loadScheduledSends();
  sends.push({ ...send, id: `s-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` });
  save(SENDS_KEY, sends);
}

export function removeScheduledSend(id: string): void {
  save(SENDS_KEY, loadScheduledSends().filter((s) => s.id !== id));
}

/** Shared "when" options for reminders and scheduled sends. */
export function whenOptions(): { label: string; at: number }[] {
  const now = Date.now();
  const tomorrow9 = new Date();
  tomorrow9.setDate(tomorrow9.getDate() + 1);
  tomorrow9.setHours(9, 0, 0, 0);
  return [
    { label: "In 30 minutes", at: now + 30 * 60_000 },
    { label: "In 1 hour", at: now + 60 * 60_000 },
    { label: "Tomorrow 9:00", at: tomorrow9.getTime() },
  ];
}

export function formatWhen(at: number): string {
  const d = new Date(at);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return time;
  return `${d.toLocaleDateString([], { day: "2-digit", month: "short" })} ${time}`;
}
